import CarouselImage1 from '@/assets/onboarding/carousel-image-1.svg?react';
import CarouselImage2 from '@/assets/onboarding/carousel-image-2.svg?react';
import CarouselImage3 from '@/assets/onboarding/carousel-image-3.svg?react';
import HeroImage from '@/assets/onboarding/hero-image.svg?react';
import { useAuthContext } from '@/context/auth-context';
import { connectColors, themeColors } from '@/theme/colors';
import { Button, UtilityButton } from '@lego/connect-components-react';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useState } from 'react';
import { OnboardingResourceItem } from './onboarding-resource-item';

type OnboardingSlide = {
  id: string;
  image: ReactNode;
  imageAlt: string;
  title: ReactNode;
  supportingText: string;
};

const SLIDES: OnboardingSlide[] = [
  {
    id: 'simulation',
    image: <CarouselImage1 />,
    imageAlt: 'Preview of the Product Pricing Overview',
    title: 'Where is my simulation?',
    supportingText:
      'Find, review, and continue your price simulations from the Simulations option under Price Planning Menu.',
  },
  {
    id: 'create-prices',
    image: <CarouselImage2 />,
    imageAlt: 'Preview of the Product Pricing Simulation',
    title: 'Simulate prices with confidence',
    supportingText:
      'Edit and review price proposals in one place, no more saving or creating versions, all happens automatically',
  },
  {
    id: 'approval',
    image: <CarouselImage3 />,
    imageAlt: 'Preview of the Product Pricing Approval flow',
    title: 'Keep pricing rounds moving',
    supportingText:
      'Use clear workflows and shared data to collaborate throughout each pricing round.',
  },
];

// TODO(connect-ds): Replace native layout when Connect provides equivalent layout
const OnboardingCarousel = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const { user } = useAuthContext();
  const total = SLIDES.length;
  const slide = SLIDES[index];
  const firstName = user?.name?.split(' ')[0] || 'there';

  if (!slide) {
    return null;
  }

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
    }),
  };

  const textVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const paginate = (newDirection: number) => {
    if (newDirection > 0 && index < total - 1) {
      setDirection(1);
      setIndex(index + 1);
    } else if (newDirection < 0 && index > 0) {
      setDirection(-1);
      setIndex(index - 1);
    }
  };

  return (
    <div
      style={{
        backgroundColor: themeColors.surface.background.base.muted,
        minHeight: '100%',
        width: '100%',
      }}
    >
      <div
        style={{
          color: 'var(--ds-color-content-inverse)',
          fontFamily: 'var(--ds-font-font-family)',
          height: '640px',
          padding: '72px 48px 184px',
          backgroundColor: connectColors.core.blue[1000],
        }}
      >
        <div
          style={{
            margin: '0 0 0 max(32px, calc((100% - 1152px) / 2))',
            maxWidth: 'min(640px, calc(100% - 532px))',
            position: 'relative',
            width: '100%',
            zIndex: 2,
          }}
        >
          <span
            style={{
              fontSize: 'var(--ds-screen-text-font-size-heading-2xl)',
              fontWeight: 'var(--ds-screen-text-font-weight-bold)',
              lineHeight: 'var(--ds-screen-text-line-height-heading-2xl)',
              margin: 0,
            }}
          >
            Welcome, {firstName}
          </span>
          <br />
          <span
            style={{
              fontSize: 'var(--ds-screen-text-font-size-body-md)',
              lineHeight: 'var(--ds-screen-text-line-height-body-md)',
              margin: '16px 0 0',
              maxWidth: '680px',
            }}
          >
            This is the new LEGO Product Pricing System, or LPPS for the acronym
            lovers. It&apos;s our brand new system to set, change and update
            prices. Centralized, fast and compliant.
          </span>
        </div>
      </div>
      <section
        style={{
          backgroundColor: themeColors.surface.background.base.default,
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgb(0 0 0 / 12%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          margin: '-320px auto 64px',
          maxWidth: '1152px',
          padding: '64px',
          width: 'calc(100% - 32px)',
        }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            gap: '40px',
            minHeight: '200px',
            position: 'relative',
            width: '100%',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontFamily: 'var(--ds-font-font-family)',
                fontSize: 'var(--ds-screen-text-font-size-heading-lg)',
                fontWeight: 'var(--ds-screen-text-font-weight-bold)',
                lineHeight: 'var(--ds-screen-text-line-height-heading-lg)',
                margin: 0,
                maxWidth: 'calc(100% - 260px)',
              }}
            >
              Changes on how you work with{' '}
              <span style={{ color: themeColors.content.primary }}>
                pricing.
              </span>{' '}
              <br />
              For the better.
            </span>
            <br />
            <span
              style={{
                fontFamily: 'var(--ds-font-font-family)',
                fontSize: 'var(--ds-screen-text-font-size-body-lg)',
                lineHeight: 'var(--ds-screen-text-line-height-body-lg)',
                margin: '16px 0 0',
              }}
            >
              We streamlined the process and made it more reliable and easier to
              collaborate in the pricing rounds.
              <br />
              We know that new ways of working can bring challenges and
              questions, and we try to answer those below.
            </span>
          </div>
          <div
            style={{
              position: 'absolute',
              right: '-32px',
              top: '-378px',
              width: 'min(500px, calc(50vw - 12px))',
              zIndex: 1,
            }}
          >
            <HeroImage
              aria-label="LEGO Product Pricing onboarding illustration"
              style={{
                display: 'block',
                height: 'auto',
                margin: 0,
                maxWidth: '500px',
                width: '100%',
              }}
            />
          </div>
        </div>

        <div
          style={{
            backgroundColor: themeColors.surface.background.base.muted,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'row',
            gap: '32px',
            padding: '32px',
            width: '100%',
          }}
        >
          <OnboardingResourceItem
            icon="location-dot"
            title="All you need in one place"
          >
            We moved away from the Excel-based shared-folder process. Explore
            the highlights of the system below.
          </OnboardingResourceItem>
          <OnboardingResourceItem
            icon="book-open"
            title="We have it documented!"
          >
            Find documentation and videos that explain how to work in the
            Pricing System.
            <Button
              as="a"
              href="https://legogroup.sharepoint.com/:u:/r/sites/LEGOProductPricingSystemHub/SitePages/HomeForBrandCentral.aspx?d=w69740b90df3e430f9ffcbcf2e4aed0b0&csf=1&web=2&share=IQCQC3RpPt8PQ5_8vPLkrtCwAe9-D9zrs-3WY3gYQOu9OwI&e=TKVJ7E"
              target="_blank"
              rel="noopener noreferrer"
              appearance="ghost"
              size="small"
              variant="primary"
              iconName="arrow-up-right-from-square"
              iconPosition="end"
              style={{ alignSelf: 'flex-end', marginTop: '8px' }}
            >
              Open documentation site
            </Button>
          </OnboardingResourceItem>
          <OnboardingResourceItem icon="hand" title="Need assistance?">
            Use our service channel to raise a ticket and we will help you as
            soon as possible.
            <Button
              as="a"
              href="https://serviceportal.corp.lego.com/sp?id=sc_cat_item_guide&sys_id=432f815e9776b6905345bad3f153af04&referrer=default"
              target="_blank"
              rel="noopener noreferrer"
              appearance="ghost"
              size="small"
              variant="primary"
              iconName="arrow-up-right-from-square"
              iconPosition="end"
              style={{ alignSelf: 'flex-end', marginTop: '8px' }}
            >
              Open a ticket
            </Button>
          </OnboardingResourceItem>
        </div>

        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            gap: '54px',
            width: '100%',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: '16px',
                marginBottom: '24px',
              }}
            >
              <UtilityButton
                iconName="chevron-left"
                aria-label="Previous slide"
                appearance="solid"
                size="small"
                disabled={index === 0}
                onClick={() => paginate(-1)}
              />
              <span
                style={{
                  fontFamily: 'var(--ds-font-font-family)',
                  fontSize: 'var(--ds-screen-text-font-size-body-sm)',
                }}
              >
                {index + 1} / {total}
              </span>
              <UtilityButton
                iconName="chevron-right"
                aria-label="Next slide"
                appearance="solid"
                size="small"
                disabled={index === total - 1}
                onClick={() => paginate(1)}
              />
            </div>
            <div
              style={{
                height: '168px',
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
              }}
            >
              <AnimatePresence initial={false} mode="sync">
                <motion.div
                  key={slide.id}
                  variants={textVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ opacity: { duration: 0.2 } }}
                  style={{ inset: 0, position: 'absolute' }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--ds-font-font-family)',
                      fontSize: 'var(--ds-screen-text-font-size-heading-md)',
                      fontWeight: 'var(--ds-screen-text-font-weight-bold)',
                      lineHeight:
                        'var(--ds-screen-text-line-height-heading-md)',
                      margin: 0,
                    }}
                  >
                    {slide.title}
                  </span>
                  <br />
                  <span
                    style={{
                      fontFamily: 'var(--ds-font-font-family)',
                      fontSize: 'var(--ds-screen-text-font-size-body-md)',
                      lineHeight: 'var(--ds-screen-text-line-height-body-md)',
                      margin: '8px 0 0',
                    }}
                  >
                    {slide.supportingText}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <div
            style={{
              backgroundColor: connectColors.core.slate[50],
              borderRadius: '12px',
              flex: 1.55,
              height: '320px',
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
            }}
          >
            <AnimatePresence initial={false} custom={direction} mode="sync">
              <motion.div
                key={slide.id}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'tween', duration: 0.3, ease: 'easeInOut' },
                  opacity: { duration: 0.2 },
                }}
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  height: '100%',
                  inset: 0,
                  justifyContent: 'center',
                  padding: '24px',
                  position: 'absolute',
                }}
              >
                {slide.image}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnboardingCarousel;
